import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { API_URLS, API_BASE_URL } from "../../constants/api-urls";
import "./TransactionCompletion.scss";
import LinkedLogisticsPanel from "../../components/moduleLogistics/LinkedLogisticsPanel";

const API_BASE_URL_MEDIA = API_BASE_URL.replace("/api/v1", "");
const getMediaUrl = (accessPath, auctionId = null) => {
  if (!accessPath) return "";
  const path = typeof accessPath === "string" ? accessPath : (accessPath && accessPath.path ? accessPath.path : "");
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/auction-media/")) return `${API_BASE_URL_MEDIA}${path}`;
  const fileName = path.split(/[/\\]/).pop();
  if (fileName && auctionId) return `${API_BASE_URL_MEDIA}/auction-media/${auctionId}/${fileName}`;
  return `${API_BASE_URL_MEDIA}/media/${fileName || ""}`;
};

export default function TransactionCompletion() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [auction, setAuction] = useState(null);
  const [payment, setPayment] = useState(null);
  const [deliveryConfirmation, setDeliveryConfirmation] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [winningBid, setWinningBid] = useState(null);
  const [buyerProfile, setBuyerProfile] = useState(null);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [user, setUser] = useState(null);
  const [isSeller, setIsSeller] = useState(false);
  const [isBuyer, setIsBuyer] = useState(false);

  useEffect(() => {
    const currentUser = StorageService.getUser();
    setUser(currentUser);
    
    if (currentUser) {
      const userTypeIds = currentUser.user_type_id ? currentUser.user_type_id.toString().split(',').map(id => parseInt(id.trim())) : [];
      const isSellerRole = currentUser.company_role === 'seller' || userTypeIds.includes(2);
      const isBuyerRole = currentUser.company_role === 'buyer' || userTypeIds.includes(1);
      setIsSeller(isSellerRole);
      setIsBuyer(isBuyerRole);
    }
    
    fetchData();
  }, [auctionId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch auction
      const auctionResponse = await CommonService.get(`${API_URLS.AUCTION.BASE}/${auctionId}`);
      if (auctionResponse && auctionResponse.success) {
        setAuction(auctionResponse.data);
        
        // Fetch seller profile
        if (auctionResponse.data.user_login_id) {
          try {
            const sellerProfileResponse = await CommonService.get(`/company/profile/${auctionResponse.data.user_login_id}`);
            if (sellerProfileResponse && sellerProfileResponse.success) {
              setSellerProfile(sellerProfileResponse.data);
            }
          } catch (err) {
            console.error("Error fetching seller profile:", err);
          }
        }
      }
      
      // Fetch delivery confirmation
      try {
        const confirmationResponse = await CommonService.get(API_URLS.AUCTION.GET_DELIVERY_CONFIRMATION(auctionId));
        if (confirmationResponse && confirmationResponse.success) {
          setDeliveryConfirmation(confirmationResponse.data);
          
          // Fetch payment
          if (confirmationResponse.data.payment_id) {
            try {
              const paymentResponse = await CommonService.get(API_URLS.AUCTION.GET_PAYMENT(confirmationResponse.data.payment_id));
              if (paymentResponse && paymentResponse.success) {
                setPayment(paymentResponse.data);
              }
            } catch (err) {
              console.error("Error fetching payment:", err);
            }
          }
          
          // Fetch buyer profile
          if (confirmationResponse.data.buyer_user_id) {
            try {
              const buyerProfileResponse = await CommonService.get(`/company/profile/${confirmationResponse.data.buyer_user_id}`);
              if (buyerProfileResponse && buyerProfileResponse.success) {
                setBuyerProfile(buyerProfileResponse.data);
              }
            } catch (err) {
              console.error("Error fetching buyer profile:", err);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching delivery confirmation:", err);
      }
      
      // Fetch winning bid
      try {
        const bidsResponse = await CommonService.get(API_URLS.AUCTION.BIDS(auctionId));
        if (bidsResponse && bidsResponse.success && bidsResponse.data) {
          const bids = Array.isArray(bidsResponse.data) ? bidsResponse.data : [];
          const winningBidData = bids.find(b => 
            b.bid_id === auctionResponse.data.winning_bid_id || 
            b.bid_status === 'won'
          );
          if (winningBidData) {
            setWinningBid(winningBidData);
          }
        }
      } catch (err) {
        console.error("Error fetching winning bid:", err);
      }
      
      // Fetch inspection (if exists)
      // Note: We'll need to add an endpoint to get inspection by auction
      // For now, we'll skip this and show "N/A" or handle gracefully
      
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load transaction data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getTimelineEvents = () => {
    const events = [];
    
    // Buyer Won
    if (winningBid?.bid_placed_at) {
      events.push({
        step: "Buyer Won",
        amount: formatCurrency(winningBid.bid_amount),
        date: formatDate(winningBid.bid_placed_at),
        completed: true
      });
    }
    
    // Inspection (if exists)
    if (inspection?.completed_at) {
      events.push({
        step: "Inspection",
        status: "Completed ✓",
        date: formatDate(inspection.completed_at),
        completed: true
      });
    } else if (auction?.inspection_allowed === 'Yes') {
      events.push({
        step: "Inspection",
        status: "Not Scheduled",
        date: "N/A",
        completed: false
      });
    }
    
    // Payment
    if (payment?.payment_completed_at) {
      events.push({
        step: "Payment",
        status: "Received ✓",
        date: formatDate(payment.payment_completed_at),
        completed: true
      });
    }
    
    // Handover
    if (deliveryConfirmation?.seller_confirmed_at && deliveryConfirmation?.buyer_confirmed_at) {
      const handoverDate = deliveryConfirmation.seller_confirmed_at > deliveryConfirmation.buyer_confirmed_at
        ? deliveryConfirmation.seller_confirmed_at
        : deliveryConfirmation.buyer_confirmed_at;
      events.push({
        step: "Handover",
        status: "Confirmed ✓",
        date: formatDate(handoverDate),
        completed: true
      });
    }
    
    // Completed
    if (deliveryConfirmation?.transaction_status === 'completed') {
      events.push({
        step: "Completed",
        status: "✓",
        date: formatDate(deliveryConfirmation.updated_at || new Date()),
        completed: true
      });
    }
    
    return events;
  };

  const getBuyerName = () => {
    if (buyerProfile?.user_name) return buyerProfile.user_name;
    if (buyerProfile?.contact_details) {
      const contactDetails = typeof buyerProfile.contact_details === 'string' 
        ? JSON.parse(buyerProfile.contact_details) 
        : buyerProfile.contact_details;
      if (contactDetails?.contact_name) return contactDetails.contact_name;
    }
    if (buyerProfile?.company_name) return buyerProfile.company_name;
    if (winningBid?.bidder_name) return winningBid.bidder_name;
    return "N/A";
  };

  const getProductImage = () => {
    if (!auction?.images) return null;
    let arr = [];
    if (Array.isArray(auction.images)) arr = auction.images;
    else if (typeof auction.images === "string") {
      try { arr = auction.images.trim().startsWith("[") ? JSON.parse(auction.images) : [auction.images]; } catch (_) { arr = [auction.images]; }
    }
    const first = arr[0];
    if (!first) return null;
    return getMediaUrl(first, auction.auction_id);
  };

  if (loading) {
    return (
      <div className="transaction-completion-page">
        <div className="loading-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!auction || !payment || !deliveryConfirmation) {
    return (
      <div className="transaction-completion-page">
        <div className="error-container">
          <p>Transaction data not found</p>
          <button onClick={() => navigate("/auction/my/bids")} className="btn-back">
            Back to My Bids
          </button>
        </div>
      </div>
    );
  }

  const timelineEvents = getTimelineEvents();
  const gstAmount = payment.gst_amount || 0;
  const winningBidAmount = payment.winning_bid_amount || 0;
  const totalAmount = payment.total_amount || (winningBidAmount + gstAmount);
  const gstPercentage = winningBidAmount > 0 ? ((gstAmount / winningBidAmount) * 100).toFixed(0) : 18;

  return (
    <div className="transaction-completion-page">
      <div className="container">
        {/* Back Button */}
        <button className="back-btn" onClick={() => navigate(-1)}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>

        {isBuyer && auctionId && String(payment?.payment_status || '').toLowerCase() === 'completed' && (
          <LinkedLogisticsPanel
            sourceModule="auction"
            sourceRecordType="winning_bid"
            sourceRecordId={String(auctionId)}
            linkPurpose="fulfillment"
            title="Shipment logistics"
          />
        )}

        {/* Page Header */}
        <div className="page-header">
          <div className="header-left">
            <div className="header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="currentColor"/>
              </svg>
            </div>
            <div>
              <h1>Transaction Complete</h1>
              <p>Successful auction completion</p>
            </div>
          </div>
        </div>

        {/* Success Banner */}
        <div className="success-banner">
          <div className="banner-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="24" fill="#28a745"/>
              <path d="M15 24L21 30L33 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="banner-content">
            <h2>Auction Successfully Completed!</h2>
            <p>The transaction has been successfully completed. Payment has been released to the seller.</p>
          </div>
        </div>

        {/* Auction Summary */}
        <div className="summary-card">
          <div className="card-header">
            <div className="header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor"/>
                <path d="M2 17L12 22L22 17V12L12 17L2 12V17Z" fill="currentColor"/>
              </svg>
            </div>
            <h3>Auction Summary</h3>
          </div>
          <div className="card-body">
            <div className="product-section">
              <div className="product-image">
                {getProductImage() ? (
                  <img src={getProductImage()} alt={auction.product_name} />
                ) : (
                  <div className="placeholder-image">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" fill="currentColor"/>
                    </svg>
                  </div>
                )}
              </div>
              <div className="product-info">
                <h4>{auction.product_name}</h4>
                <p className="description">{auction.product_description || "No description available"}</p>
                <div className="product-details">
                  <div className="detail-row">
                    <span className="label">Category:</span>
                    <span className="value">{auction.category_name || "N/A"}</span>
                  </div>
                  {auction.product_attributes && (() => {
                    const attributes = typeof auction.product_attributes === 'string' 
                      ? JSON.parse(auction.product_attributes) 
                      : auction.product_attributes;
                    return (
                      <>
                        {attributes.condition && (
                          <div className="detail-row">
                            <span className="label">Condition:</span>
                            <span className="value">{attributes.condition}</span>
                          </div>
                        )}
                        {attributes.year && (
                          <div className="detail-row">
                            <span className="label">Year:</span>
                            <span className="value">{attributes.year}</span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
            <div className="winning-buyer-section">
              <h4>Winning Buyer</h4>
              <div className="buyer-info">
                <div className="buyer-name">{getBuyerName()}</div>
                <div className="buyer-amount">{formatCurrency(winningBidAmount)}</div>
                <div className="buyer-total">Including GST ({gstPercentage}%): {formatCurrency(totalAmount)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Timeline */}
        <div className="timeline-card">
          <div className="card-header">
            <div className="header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
              </svg>
            </div>
            <h3>Transaction Timeline</h3>
          </div>
          <div className="timeline-content">
            <div className="timeline-steps">
              {timelineEvents.map((event, index) => (
                <div key={index} className={`timeline-step ${event.completed ? 'completed' : ''}`}>
                  <div className="step-icon">
                    {event.completed ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="12" fill="#28a745"/>
                        <path d="M7 12L10 15L17 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="12" fill="#e5e7eb"/>
                      </svg>
                    )}
                  </div>
                  <div className="step-content">
                    <div className="step-title">{event.step}</div>
                    {event.amount && <div className="step-amount">{event.amount}</div>}
                    {event.status && <div className="step-status">{event.status}</div>}
                    <div className="step-date">{event.date}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="financial-card">
          <div className="card-header">
            <div className="header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12.5 6V11H17V13H12.5V18H10.5V13H6V11H10.5V6H12.5Z" fill="currentColor"/>
              </svg>
            </div>
            <h3>Financial Summary</h3>
          </div>
          <div className="financial-content">
            <div className="financial-items">
              <div className="financial-item winning-bid">
                <span className="label">Winning Bid</span>
                <span className="value">{formatCurrency(winningBidAmount)}</span>
              </div>
              <div className="financial-item gst">
                <span className="label">GST ({gstPercentage}%)</span>
                <span className="value">{formatCurrency(gstAmount)}</span>
              </div>
              <div className="financial-item total">
                <span className="label">Total Amount</span>
                <span className="value">{formatCurrency(totalAmount)}</span>
              </div>
            </div>
            <div className="financial-details">
              <div className="detail-item">
                <span className="label">Transaction ID:</span>
                <span className="value">TXN-AUCTION-{auction.auction_id}</span>
              </div>
              <div className="detail-item">
                <span className="label">Payment Status:</span>
                <span className="value status-released">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="8" cy="8" r="8" fill="#28a745"/>
                    <path d="M5 8L7 10L11 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Released to Seller
                </span>
              </div>
              <div className="detail-item">
                <span className="label">Completion Date:</span>
                <span className="value">{formatDate(deliveryConfirmation.updated_at || new Date())}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Role-Specific Sections */}
        <div className="role-sections">
          {/* Buyer Section */}
          {isBuyer && (
            <div className="role-card buyer-card">
              <div className="card-header">
                <h3>For Buyer: {getBuyerName()}</h3>
              </div>
              <div className="checklist">
                <div className="checklist-item completed">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="#28a745"/>
                    <path d="M6 10L8 12L14 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Product successfully received</span>
                </div>
                <div className="checklist-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="9" stroke="#28a745" strokeWidth="2" fill="white"/>
                  </svg>
                  <span>Leave feedback for seller</span>
                </div>
                <div className="checklist-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="9" stroke="#28a745" strokeWidth="2" fill="white"/>
                  </svg>
                  <span>Download invoice and transaction documents</span>
                </div>
                <div className="checklist-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="9" stroke="#28a745" strokeWidth="2" fill="white"/>
                  </svg>
                  <span>Keep transaction records for warranty</span>
                </div>
              </div>
              <button className="btn-download">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 2V12M10 12L6 8M10 12L14 8M2 14V16C2 17.1 2.9 18 4 18H16C17.1 18 18 17.1 18 16V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Download Transaction Report
              </button>
            </div>
          )}

          {/* Seller Section */}
          {isSeller && (
            <div className="role-card seller-card">
              <div className="card-header">
                <h3>For Seller: {sellerProfile?.company_name || "N/A"}</h3>
              </div>
              <div className="checklist">
                <div className="checklist-item completed">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="#28a745"/>
                    <path d="M6 10L8 12L14 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Product successfully handed over</span>
                </div>
                <div className="checklist-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="9" stroke="#28a745" strokeWidth="2" fill="white"/>
                  </svg>
                  <span>Leave feedback for buyer</span>
                </div>
                <div className="checklist-item completed">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="10" fill="#28a745"/>
                    <path d="M6 10L8 12L14 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Payment settlement completed</span>
                </div>
                <div className="checklist-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="9" stroke="#28a745" strokeWidth="2" fill="white"/>
                  </svg>
                  <span>Download transaction documents</span>
                </div>
              </div>
              <button className="btn-share">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 8C16.1 8 17 7.1 17 6C17 4.9 16.1 4 15 4C13.9 4 13 4.9 13 6C13 6.3 13.1 6.6 13.2 6.9L8.8 9.1C8.3 8.4 7.5 8 6.5 8C4.6 8 3 9.6 3 11.5C3 13.4 4.6 15 6.5 15C7.5 15 8.3 14.6 8.8 13.9L13.2 16.1C13.1 16.4 13 16.7 13 17C13 18.1 13.9 19 15 19C16.1 19 17 18.1 17 17C17 15.9 16.1 15 15 15C14 15 13.2 15.4 12.7 16.1L8.3 13.9C8.4 13.6 8.5 13.3 8.5 13C8.5 12.7 8.4 12.4 8.3 12.1L12.7 9.9C13.2 10.6 14 11 15 11Z" fill="currentColor"/>
                </svg>
                Share Completion Certificate
              </button>
            </div>
          )}
        </div>

        {/* Congratulations Message */}
        <div className="congratulations-banner">
          <div className="banner-icon">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="16" cy="16" r="16" fill="#28a745"/>
              <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="banner-content">
            <h3>Congratulations! This auction transaction has been completed successfully.</h3>
            <p>Trocanet facilitated this transaction as a neutral digital platform. Both parties have confirmed completion, and payment has been released to the seller. Thank you for using Trocanet!</p>
          </div>
        </div>
      </div>
    </div>
  );
}
